import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, KeyRound, Lock, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Axios is used with credentials so the HttpOnly session cookie is sent.
const client = axios.create({ baseURL: API, withCredentials: true, timeout: 15000 });

const CTX_STATES = { CHECKING: 'checking', LOCKED: 'locked', UNLOCKED: 'unlocked' };

export const LicenseContext = React.createContext({
  state: CTX_STATES.CHECKING,
  isOwner: false,
  deactivate: () => {},
});

export default function LicenseGate({ children }) {
  const [state, setState] = useState(CTX_STATES.CHECKING);
  const [configured, setConfigured] = useState(true);
  const [isOwner, setIsOwner] = useState(false);
  const [licenseKey, setLicenseKey] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const checkStatus = useCallback(async () => {
    try {
      const r = await client.get('/license/status');
      setConfigured(r.data.configured !== false);
      setIsOwner(!!r.data.owner);
      setState(r.data.active ? CTX_STATES.UNLOCKED : CTX_STATES.LOCKED);
    } catch (e) {
      setConfigured(false);
      setIsOwner(false);
      setState(CTX_STATES.LOCKED);
    }
  }, []);

  useEffect(() => { checkStatus(); }, [checkStatus]);

  const activate = async (e) => {
    e && e.preventDefault && e.preventDefault();
    setError('');
    const trimmed = (licenseKey || '').trim();
    if (!trimmed) { setError('Please enter your license key.'); return; }
    setSubmitting(true);
    try {
      await client.post('/license/activate', { license_key: trimmed });
      setLicenseKey('');
      await checkStatus();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 429) setError('Too many attempts. Please wait a few minutes and try again.');
      else if (status === 409) setError(detail || 'This license is already active on the maximum number of devices.');
      else if (status === 503) setError('Activation is temporarily unavailable. Please try again in a moment.');
      else if (typeof detail === 'string') setError(detail);
      else setError('This license could not be activated. Please double-check the key from your Payhip receipt.');
    } finally {
      setSubmitting(false);
    }
  };

  const deactivate = useCallback(async () => {
    try { await client.post('/license/deactivate'); } catch {}
    setState(CTX_STATES.LOCKED);
  }, []);

  if (state === CTX_STATES.CHECKING) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-black text-zinc-300" data-testid="license-checking">
        <Loader2 className="animate-spin" size={22} />
      </div>
    );
  }

  if (state === CTX_STATES.LOCKED) {
    return (
      <ActivationScreen
        licenseKey={licenseKey}
        setLicenseKey={setLicenseKey}
        onSubmit={activate}
        submitting={submitting}
        error={error}
        configured={configured}
      />
    );
  }

  return (
    <LicenseContext.Provider value={{ state, isOwner, deactivate }}>
      {children}
    </LicenseContext.Provider>
  );
}


function ActivationScreen({ licenseKey, setLicenseKey, onSubmit, submitting, error, configured }) {
  return (
    <main
      data-testid="activation-screen"
      className="min-h-screen w-full bg-black text-zinc-100 flex items-center justify-center px-4 sm:px-6 py-10 overflow-x-hidden noise"
    >
      <div className="w-full max-w-[520px] mx-auto">
        <div className="text-center mb-8">
          <div className="eyebrow inline-flex items-center gap-2 justify-center">
            <Lock size={12} /> Licensed Access
          </div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-widest uppercase mt-3">Type Elite Studio</h1>
          <p className="mt-3 text-lg text-zinc-300 font-display">Activate Your Studio</p>
        </div>

        <form
          onSubmit={onSubmit}
          className="rounded-3xl chrome-border bg-zinc-900/60 backdrop-blur p-6 sm:p-8 space-y-5"
          data-testid="activation-form"
        >
          <p className="text-sm text-zinc-300">
            Enter the unique license key included with your Payhip purchase.
          </p>

          <label className="block">
            <span className="eyebrow flex items-center gap-2"><KeyRound size={12} /> License Key</span>
            <input
              data-testid="license-key-input"
              type="text"
              inputMode="text"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              value={licenseKey}
              onChange={(e) => setLicenseKey(e.target.value)}
              placeholder="XXXXX-XXXXX-XXXXX-XXXXX"
              className="mt-2 w-full bg-zinc-950 border border-zinc-800 focus:border-pink-500 outline-none rounded-lg px-4 py-3 text-base tracking-wider font-mono uppercase placeholder:normal-case placeholder:font-sans placeholder:text-zinc-600"
              disabled={submitting}
            />
          </label>

          {error && (
            <div
              data-testid="activation-error"
              role="alert"
              className="rounded-lg border border-pink-500/40 bg-pink-500/10 text-pink-200 text-sm px-4 py-3"
            >
              {error}
            </div>
          )}

          {!configured && (
            <div
              data-testid="activation-not-configured"
              role="alert"
              className="rounded-lg border border-yellow-500/40 bg-yellow-500/10 text-yellow-100 text-xs px-4 py-3"
            >
              License gate is not yet configured on the server. Please contact the seller.
            </div>
          )}

          <button
            data-testid="activation-submit"
            type="submit"
            disabled={submitting}
            className="btn-magenta w-full text-white text-xs sm:text-sm px-6 py-3 rounded-full uppercase tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {submitting ? (<><Loader2 size={16} className="animate-spin" /> Activating…</>) : (<><ShieldCheck size={16} /> Activate Studio</>)}
          </button>

          <p className="text-xs text-zinc-400 pt-1 leading-relaxed">
            Your purchase includes unlimited TYPE ELITE Studio use. Third-party AI platforms may charge separately.
          </p>
          <p className="text-xs text-zinc-500 leading-relaxed">
            Can’t find your key? Check your Payhip receipt or contact the seller through Payhip.
          </p>
        </form>

        <p className="text-center text-[10px] uppercase tracking-widest text-zinc-600 mt-6">
          Secure activation · same-origin · no account required
        </p>

        <div className="text-center mt-4">
          <a
            data-testid="owner-access-link"
            href="/admin"
            className="text-[11px] uppercase tracking-widest text-zinc-500 hover:text-white transition-colors inline-flex items-center gap-1.5"
          >
            <Lock size={11} /> Owner Access
          </a>
        </div>
      </div>
    </main>
  );
}
